/**
 * useAudioAnalyser.js
 * 
 * A performant audio analysis hook optimized for iOS Safari.
 * Extracts volume/amplitude data from a MediaStream for driving
 * visual animations (orb reactivity).
 * 
 * Uses Web Audio API AnalyserNode with performance optimizations:
 * - Throttled updates via requestAnimationFrame
 * - Small FFT size for efficiency
 * - Smoothed output to prevent jittery animations
 */

import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for analyzing audio volume from a MediaStream
 * 
 * @param {MediaStream} stream - The audio stream to analyze (e.g., remote WebRTC audio)
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether analysis is active (default: true)
 * @param {number} options.smoothingFactor - How much to smooth volume changes 0-1 (default: 0.8)
 * @param {number} options.fftSize - FFT size for frequency analysis (default: 256 for performance)
 * @returns {Object} { volume, isActive, frequencyData }
 */
export const useAudioAnalyser = (stream, options = {}) => {
  const {
    enabled = true,
    smoothingFactor = 0.8,
    fftSize = 256
  } = options;

  // Volume state (0-1 normalized)
  const [volume, setVolume] = useState(0);
  const [isActive, setIsActive] = useState(false);
  
  // Refs for Web Audio API objects (don't trigger re-renders)
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationFrameRef = useRef(null);
  const smoothedVolumeRef = useRef(0);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch (e) {
        // Already disconnected
      }
      sourceRef.current = null;
    }
    
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch (e) {
        // Already disconnected
      }
      analyserRef.current = null;
    }
    
    // Don't close AudioContext immediately - may cause issues on iOS
    // Let it be garbage collected
    audioContextRef.current = null;
    dataArrayRef.current = null;
    
    setVolume(0);
    setIsActive(false);
    smoothedVolumeRef.current = 0;
  }, []);

  // Analysis loop - runs on requestAnimationFrame for smooth 60fps updates
  const analyzeAudio = useCallback(() => {
    if (!analyserRef.current || !dataArrayRef.current) return;

    // Get frequency data
    analyserRef.current.getByteFrequencyData(dataArrayRef.current);
    
    // Calculate RMS (root mean square) for more accurate volume representation
    let sum = 0;
    const data = dataArrayRef.current;
    const length = data.length;
    
    for (let i = 0; i < length; i++) {
      sum += data[i] * data[i];
    }
    
    const rms = Math.sqrt(sum / length);
    
    // Normalize to 0-1 range (frequency data is 0-255)
    const normalizedVolume = Math.min(rms / 128, 1);
    
    // Apply smoothing to prevent jittery animations
    // Faster attack (volume goes up quickly), slower release (fades smoothly)
    const currentSmoothed = smoothedVolumeRef.current;
    const attackFactor = 0.4; // How fast volume rises
    const releaseFactor = smoothingFactor; // How fast volume falls
    
    const factor = normalizedVolume > currentSmoothed ? attackFactor : releaseFactor;
    const smoothedVolume = currentSmoothed * factor + normalizedVolume * (1 - factor);
    
    smoothedVolumeRef.current = smoothedVolume;
    setVolume(smoothedVolume);
    
    // Continue the loop
    animationFrameRef.current = requestAnimationFrame(analyzeAudio);
  }, [smoothingFactor]);

  // Setup audio analysis when stream changes
  useEffect(() => {
    if (!stream || !enabled) {
      cleanup();
      return;
    }

    // Check if stream has audio tracks
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      console.log('[AudioAnalyser] No audio tracks in stream');
      return;
    }

    console.log('[AudioAnalyser] Setting up audio analysis');

    try {
      // Create AudioContext (use webkitAudioContext for iOS Safari)
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContextClass();
      audioContextRef.current = audioContext;

      // iOS Safari requires user interaction to start AudioContext
      // If suspended, we'll resume it when we get audio
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(err => {
          console.warn('[AudioAnalyser] Could not resume AudioContext:', err);
        });
      }

      // Create AnalyserNode
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = fftSize;
      analyser.smoothingTimeConstant = 0.3; // Some built-in smoothing
      analyserRef.current = analyser;

      // Create data array for frequency data
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);

      // Create MediaStreamSource from the stream
      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;

      // Connect source -> analyser (no output to speakers - stream handles that)
      source.connect(analyser);

      setIsActive(true);
      console.log('[AudioAnalyser] Audio analysis started');

      // Start the analysis loop
      animationFrameRef.current = requestAnimationFrame(analyzeAudio);

    } catch (error) {
      console.error('[AudioAnalyser] Failed to setup audio analysis:', error);
      cleanup();
    }

    return cleanup;
  }, [stream, enabled, fftSize, analyzeAudio, cleanup]);

  return {
    volume,      // 0-1 normalized volume level
    isActive     // Whether analysis is running
  };
};

export default useAudioAnalyser;
